import { FC, useState } from 'react';
import {
  FormControl,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { FontGroup } from '../utils/fontUtils';
import BrandBadge from '../../BrandBadge';
import { Flex } from '@chakra-ui/react';
import { BrandVariable } from '@adflow/types';

interface Props {
  value: string;
  fonts: FontGroup[];
  standardFonts: string[];
  customFonts: { fontFamily: string }[];
  primaryFont?: string | null;
  secondaryFont?: string | null;
  onChange: (family: string) => void;
}

const getFontLabel = (
  family: string,
  standardFonts: string[],
  customFonts: { fontFamily: string }[],
  primaryFont?: string | null,
  secondaryFont?: string | null,
) => {
  if ((primaryFont && primaryFont === family) || (secondaryFont && secondaryFont === family))
    return '';
  if (standardFonts.includes(family)) return ' (Standard)';
  if (customFonts.some((f) => f.fontFamily === family)) return ' (Custom)';
  return ' (Not installed)';
};

const containsText = (text: string, searchText: string) =>
  text.toLowerCase().includes(searchText.toLowerCase());

export const FontFamilySelect: FC<Props> = ({
  value,
  fonts,
  standardFonts,
  customFonts,
  primaryFont,
  secondaryFont,
  onChange,
}) => {
  const [searchText, setSearchText] = useState('');

  const displayedFonts = searchText
    ? fonts.filter(({ family }) => containsText(family, searchText))
    : fonts;

  return (
    <FormControl fullWidth>
      <Select
        size="small"
        aria-label="Font family"
        value={value}
        onClose={() => setSearchText('')}
        displayEmpty
        renderValue={(selected) => {
          if (!selected) return <Typography color="text.secondary">Select Font Family</Typography>;
          const label = getFontLabel(
            selected as string,
            standardFonts,
            customFonts,
            primaryFont,
            secondaryFont,
          );
          return (
            <Flex alignItems="center" gap={1}>
              <Typography>
                {selected as string} {label}
              </Typography>
              {primaryFont && primaryFont === (selected as string) && (
                <BrandBadge variableType={BrandVariable.PRIMARY_FONT} />
              )}
              {secondaryFont && secondaryFont === (selected as string) && (
                <BrandBadge variableType={BrandVariable.SECONDARY_FONT} />
              )}
            </Flex>
          );
        }}
        MenuProps={{
          autoFocus: false,
          PaperProps: {
            style: {
              maxHeight: '300px',
              overflowY: 'auto',
            },
          },
        }}
      >
        <ListSubheader>
          <TextField
            size="small"
            autoFocus
            placeholder="Search fonts..."
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Escape') e.stopPropagation();
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </ListSubheader>

        {displayedFonts.map(({ family }) => {
          const label = getFontLabel(
            family,
            standardFonts,
            customFonts,
            primaryFont,
            secondaryFont,
          );
          return (
            <MenuItem key={family} value={family} onClick={() => onChange(family)}>
              <Flex alignItems="center" gap={1}>
                <Typography>
                  {family} {label}
                </Typography>
                {primaryFont && primaryFont === family && (
                  <BrandBadge variableType={BrandVariable.PRIMARY_FONT} />
                )}
                {secondaryFont && secondaryFont === family && (
                  <BrandBadge variableType={BrandVariable.SECONDARY_FONT} />
                )}
              </Flex>
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};
